import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import WaterTemp from 'components/Icons/WaterTemp';
import CurrentWeatherConditions from 'components/CurrentWeatherConditions';
import type { Units } from 'types/units';
import { useTreatments } from 'utils/treatments';
import { SL_WEB_SUNBUM_SPONSORSHIP } from 'common/treatments';
import getRecommendedSpfFromWeatherIcon from './utils/getRecommendedSpfFromWeatherIcon';
import SunBumSpfButton, { type SunBumSpfLevels } from './SunBumSpfButton';

import styles from './CurrentWaterTemperatures.module.scss';

interface Props {
  units: Units;
  waterTemp: {
    max: number;
    min: number;
  };
  weather: {
    condition: string;
    temperature: number;
  };
  weatherIconPath: string;
  wetsuit: {
    thickness: string;
    type: string;
  };
}

const CurrentWaterTemperaturesContents: React.FC<Props> = ({
  units,
  waterTemp,
  weather,
  weatherIconPath,
  wetsuit,
}) => {
  const treatments = useTreatments();

  const recommendedSpfLevel = useMemo(
    () => getRecommendedSpfFromWeatherIcon(weather.condition),
    [weather.condition],
  );

  const showSunBumButton = useMemo(
    () => treatments[SL_WEB_SUNBUM_SPONSORSHIP] === 'on' && recommendedSpfLevel,
    [recommendedSpfLevel, treatments],
  );

  return (
    <Box className={styles.wrapper}>
      <Box className={styles.content}>
        <Stack direction="row" className={styles.readings}>
          <Box>
            <Stack direction="row" className={styles.water} data-testid="current-water-temp-units">
              <Box className={styles.icon}>
                <WaterTemp />
              </Box>
              <Typography component="p">
                <Typography variant="title2" component="span" className={styles.waterTemp}>
                  {waterTemp?.max}
                </Typography>
                <Typography variant="headline" component="span" className={styles.waterUnits}>
                  °{units?.temperature}
                </Typography>
              </Typography>
            </Stack>
          </Box>
          <Box>
            <CurrentWeatherConditions
              spotName="spotName"
              weatherIconPath={weatherIconPath}
              weather={weather}
              units={units?.temperature}
            />
          </Box>
        </Stack>
        <Box className={styles.wetsuit}>
          <Typography variant="callout2" component="p" className={styles.wetsuitText}>
            {wetsuit?.thickness === '0mm' ? wetsuit?.type : `${wetsuit?.thickness} wetsuit`}
          </Typography>
        </Box>
      </Box>
      {showSunBumButton && recommendedSpfLevel && (
        <Box className={styles.sunBumContainer}>
          <SunBumSpfButton spfLevel={recommendedSpfLevel as SunBumSpfLevels} />
        </Box>
      )}
    </Box>
  );
};

interface CurrentWetsuitWaterTempWeatherProps extends Props {
  error?: boolean;
}

export const CurrentWaterTemperatures: React.FC<CurrentWetsuitWaterTempWeatherProps> = (props) => {
  const { error } = props;
  return (
    <Box data-testid="current-wetsuit-water-temp-weather-card" className={styles.card}>
      <Box className={styles.cardContent}>
        <ErrorBoundary
          error={error}
          errorBoundaryId="current-water-temp-weather"
          render={() => (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              data-testid="current-weather-conditions-error"
            >
              <Typography variant="callout1" color="text.brandSecondary">
                Something went wrong here.
              </Typography>
            </Stack>
          )}
        >
          <CurrentWaterTemperaturesContents {...props} />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

export default CurrentWaterTemperatures;
